// @flow
import client from '../../client'
import { config } from '../../config'
import logError from 'mz-utils/logError'
import omitEmptyFields from 'mz-utils/omitEmptyFields'
import prebookSearchResultBuilder from './builders/prebookSearchResult'
import ondemandSearchResultBuilder from './builders/ondemandSearchResult'
import format from '../../utils/format'
import { poller } from '../../utils/poller'
import {
  filterSearchQuery,
  isMoreComing,
  isResultEmpty,
  isOndemandResultEmpty,
  resolvePlaces,
} from './utils'

// Types
import type { SearchResult } from '../../models/search'
import type {
  OndemandSearchResponse,
  PrebookSearchResponse,
} from '../../api_types'

type SearchLocation = {
  lat?: number,
  lng?: number,
  address: string,
  google_id?: string,
  custom_name?: string,
  terminal?: string,
  airport_name?: string,
  is_hotel?: boolean,
  favorite_id?: number,
  favorite_source?: string,
}
export type Options = {
  start_location?: ?SearchLocation,
  end_location?: ?SearchLocation,
  currency?: string,
  marketing?: {
    campaign?: string,
    branch?: string,
  },
  provider_sources?: string[],
  pickup_datetime?: string,
  mode: string,
  num_passengers?: number,
  return_pickup_datetime?: string,
  domain?: string,
  hourly_booking_duration?: number,
  corporate_trip_id?: string,
  corp_id_list?: string[],
  sources?: ('prebook' | 'ondemand')[],
}
type PollerResult = {
  name: string,
  result: SearchResult | null,
  error: any,
}

export type PullOptions = {
  pullAll: boolean,
}

export type SearchFunc = ((throttle?: number) => Promise<SearchResult>) & {
  cancel: () => void,
}

// API Endpoints
export const PREBOOK_POOL_ENDPOINT = '/search/{0}/poll/'
export const PREBOOK_TRIP_GET_ENDPOINT = '/search/'
export const ONDEMAND_POOL_ENDPOINT = '/ondemand/search/{0}/poll/'
export const ONDEMAND_TRIP_GET_ENDPOINT = '/ondemand/search/'

function createPrebookLocationQuery(location: any, type: string) {
  if (!location) return null
  return omitEmptyFields(
    {
      [`${type}_address`]: location.address,
      [`${type}_id`]: location.place_id || location.google_id,
      [`${type}_name`]:
        location.custom_name || location.display || location.name,
      [`${type}_lat`]: location.lat,
      [`${type}_lng`]: location.lng,
      [`${type}_terminal`]: location.terminal,
      [`${type}_favorite_id`]: location.favorite_id,
      [`${type}_favorite_source`]: location.favorite_source,
    },
    { string: true }
  )
}

export const searchPrebook = (
  params: Options,
  pollerOptions?: PullOptions
): SearchFunc => {
  const query = filterSearchQuery(params)

  return poller({
    initPoll: async () => {
      const {
        start_location,
        end_location,
        marketing,
        ...queryRest
      } = await resolvePlaces(query)

      const endpoint = format(PREBOOK_TRIP_GET_ENDPOINT)
      return client.post(endpoint, {
        baseUrl: config.BASE_URL_SEARCH,
        body: {
          ...queryRest,
          ...marketing,
          ...createPrebookLocationQuery(start_location, 'start'),
          ...createPrebookLocationQuery(end_location, 'end'),
        },
      })
    },
    pollNext: (id) =>
      client.get(format(PREBOOK_POOL_ENDPOINT, id), {
        baseUrl: config.BASE_URL_SEARCH,
      }),
    builder: (response: PrebookSearchResponse) =>
      prebookSearchResultBuilder(response),
    isMoreComing,
    isResultEmpty,
    pollAll: !!pollerOptions?.pullAll,
  })
}

export const searchOndemand = (
  params: Options,
  pollerOptions?: PullOptions
): SearchFunc => {
  const query = filterSearchQuery(params)

  return poller({
    initPoll: async () => {
      const fullQuery = await resolvePlaces(query)
      const endpoint = format(ONDEMAND_TRIP_GET_ENDPOINT)
      return client.post(endpoint, { body: fullQuery })
    },
    pollNext: (id) => client.get(format(ONDEMAND_POOL_ENDPOINT, id)),
    builder: (response: OndemandSearchResponse) =>
      ondemandSearchResultBuilder(response),
    isMoreComing,
    isResultEmpty: isOndemandResultEmpty,
    pollAll: !!pollerOptions?.pullAll,
  })
}

function createNamedPoller(name, searchPoller) {
  return async (...args): Promise<PollerResult> => {
    try {
      const result = await searchPoller(...args)
      return { name, result, error: null }
    } catch (e) {
      return { name, result: null, error: e }
    }
  }
}

export function search(
  { sources, ...params }: Options,
  pollerOptions?: PullOptions
) {
  const pollers = {}
  const runningPollers: { [key: string]: PollerResult } = {}
  const completePollers = {}
  const pollingErrors = []

  if (sources && sources.includes('prebook')) {
    pollers.prebook = createNamedPoller(
      'prebook',
      searchPrebook(params, pollerOptions)
    )
  }
  if (sources && sources.includes('ondemand')) {
    pollers.ondemand = createNamedPoller(
      'ondemand',
      searchOndemand(params, pollerOptions)
    )
  }

  const combinedPoller = async (...pollerParams: any) => {
    Object.keys(pollers).forEach((k) => {
      if (!completePollers[k]) {
        runningPollers[k] = runningPollers[k] || pollers[k](...pollerParams)
      }
    })

    // Run the all the pollers and handle the result
    const { name, result, error }: PollerResult = (await Promise.race(
      Object.values(runningPollers)
    ): any)
    const iterationResult = result
    delete runningPollers[name]
    if (result === null) {
      completePollers[name] = true
    }
    if (error) {
      pollingErrors.push(error)
    }

    // In case when some poller finished but others are not
    // do not return null but trigger another iteration that will
    // ignore the complete poller and return results from the other one
    const pollingFullyComplete =
      Object.keys(completePollers).length === Object.keys(pollers).length

    if (!pollingFullyComplete && iterationResult === null) {
      return combinedPoller(...pollerParams)
    }

    // Log polling errors at the end of the polling
    if (pollingFullyComplete && pollingErrors.length > 0) {
      pollingErrors.forEach((e) => logError(e))
    }

    return iterationResult
  }

  // Wrap cancel function of all the pollers
  combinedPoller.cancel = () => {
    Object.keys(pollers).forEach((k) => {
      pollers[k].cancel && pollers[k].cancel()
    })
  }

  return combinedPoller
}

export default searchPrebook
